import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { useTranslation } from 'react-i18next';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
  CheckBoxFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import organisationRolesService from '../../organisationRoles/services/organisationRolesService';
import ReadOnlyFieldValueComponent from '../../app/components/ReadOnlyFieldValueComponent';
import { generateErrorsForRequiredFields } from '../../app/lib/validationHelper';
import { normalizeTag } from '../../app/helpers/normalizeTag';
import { TagExplainer } from '../../app/styles';

const FORM_NAME = 'relationType';
const REQUIRED_FIELDS = ['name', 'tag'];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const RelationTypeForm = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  editMode,
  dispatchOrganisationRolesGet,
  organisationRolesFetch,
}) => {
  const { t } = useTranslation();

  useEffect(() => dispatchOrganisationRolesGet(undefined, t), [dispatchOrganisationRolesGet, t]);

  const roleOptions = useMemo(
    () =>
      (organisationRolesFetch.value?.organisationRoles || []).map(r => ({
        key: r.id,
        value: r.id,
        text: r.name,
      })),
    [organisationRolesFetch.value]
  );

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      <Grid>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('name')}
            name="name"
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={
              <span>
                {t('tag')}
                &nbsp;
                <TagExplainer>({t('tagExplainer')})</TagExplainer>
              </span>
            }
            name="tag"
            normalize={normalizeTag}
            valueComponent={editMode ? ReadOnlyFieldValueComponent : InputFieldValueComponent}
            disabled={submitProcessing || dataLoading || editMode}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            search
            clearable
            fluid
            multiple
            label={t('sourceOrganisationRoles')}
            name="sourceRestrictions"
            valueComponent={SelectFieldValueComponent}
            options={roleOptions}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            search
            clearable
            fluid
            multiple
            label={t('targetOrganisationRoles')}
            name="targetRestrictions"
            valueComponent={SelectFieldValueComponent}
            options={roleOptions}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            toggle
            label={t('isGloballyManaged')}
            name="isGloballyManaged"
            valueComponent={CheckBoxFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

RelationTypeForm.propTypes = {
  dispatchOrganisationRolesGet: PropTypes.func.isRequired,
  organisationRolesFetch: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
  editMode: PropTypes.bool,
};

RelationTypeForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  data: {},
  editMode: false,
};

export default organisationRolesService(RelationTypeForm);
